/**
 * @file Filters.tsx
 * @description Shared filters component for bikeparks and snowparks maps
 * Handles search, region, rating, infrastructure, styles, levels, difficulty, and price filtering
 * @module components/Filters
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { Bikepark, Snowpark } from '@/types';
import {
  UtensilsCrossed,
  ParkingCircle,
  Bike,
  Heart,
  CableCar,
  Users,
  Hotel,
  Wrench,
  GraduationCap,
  ShoppingCart,
  Bath,
  Tent,
  Lock,
  Wifi,
  Cross,
  Droplets,
  Camera,
  PartyPopper,
  Circle,
  DollarSign,
  Gem
} from 'lucide-react';

interface FiltersProps {
  parks: (Bikepark | Snowpark)[];
  onFilterChange: (filtered: (Bikepark | Snowpark)[]) => void;
  type: 'bikepark' | 'snowpark';
  onResetRef?: React.MutableRefObject<(() => void) | null>;
}

interface Infrastructure {
  lift?: boolean;
  familyFriendly?: boolean;
  restaurant?: boolean;
  accommodation?: boolean;
  bikeService?: boolean;
  school?: boolean;
  parking?: boolean;
  shop?: boolean;
  rental?: boolean;
  toilet?: boolean;
  camping?: boolean;
  locker?: boolean;
  wifi?: boolean;
  firstAid?: boolean;
  washStation?: boolean;
  photography?: boolean;
  events?: boolean;
}

// Hardcoded translations based on type
const translations = {
  bikepark: {
    search: 'Szukaj',
    searchPlaceholder: 'Szukaj parku...',
    region: 'Region',
    allRegions: 'Wszystkie regiony',
    rating: 'Ocena',
    all: 'Wszystkie',
    infrastructure: 'Infrastruktura',
    lift: 'Wyciąg',
    familyFriendly: 'Dla rodzin',
    restaurant: 'Restauracja',
    accommodation: 'Noclegi',
    bikeService: 'Serwis',
    school: 'Szkoła',
    parking: 'Parking',
    shop: 'Sklep',
    rental: 'Wypożyczalnia',
    toilet: 'Toaleta',
    camping: 'Kemping',
    locker: 'Szatnia',
    wifi: 'WiFi',
    medic: 'Medyk',
    washStation: 'Myjnia',
    photoSpots: 'Fotobudka',
    events: 'Wydarzenia',
    styles: 'Style',
    levels: 'Poziomy',
    trailDifficulty: 'Trudność tras',
    green: 'Zielona',
    blue: 'Niebieska',
    red: 'Czerwona',
    black: 'Czarna',
    priceRange: 'Cena',
    free: 'Darmowe',
  },
  snowpark: {
    search: 'Search',
    searchPlaceholder: 'Search park...',
    region: 'Region',
    allRegions: 'All regions',
    rating: 'Rating',
    all: 'All',
    infrastructure: 'Infrastructure',
    lift: 'Lift',
    familyFriendly: 'Family',
    restaurant: 'Restaurant',
    accommodation: 'Accommodation',
    bikeService: 'Service',
    school: 'School',
    parking: 'Parking',
    shop: 'Shop',
    rental: 'Rental',
    toilet: 'Toilet',
    camping: 'Camping',
    locker: 'Locker',
    wifi: 'WiFi',
    medic: 'Medic',
    washStation: 'Wash',
    photoSpots: 'Photo spots',
    events: 'Events',
    styles: 'Styles',
    levels: 'Levels',
    trailDifficulty: 'Trail difficulty',
    green: 'Green',
    blue: 'Blue',
    red: 'Red',
    black: 'Black',
    priceRange: 'Price',
    free: 'Free',
  },
};

export default function Filters({ parks, onFilterChange, type, onResetRef }: FiltersProps) {
  const t = (key: string) => translations[type][key as keyof typeof translations.bikepark];

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();


  // Initialize from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [hasLift, setHasLift] = useState<boolean | null>(null);
  const [familyFriendly, setFamilyFriendly] = useState<boolean | null>(null);
  const [restaurant, setRestaurant] = useState<boolean | null>(null);
  const [accommodation, setAccommodation] = useState<boolean | null>(null);
  const [bikeService, setBikeService] = useState<boolean | null>(null);
  const [school, setSchool] = useState<boolean | null>(null);
  const [parking, setParking] = useState<boolean | null>(null);
  const [shop, setShop] = useState<boolean | null>(null);
  const [rental, setRental] = useState<boolean | null>(null);
  const [toilet, setToilet] = useState<boolean | null>(null);
  const [camping, setCamping] = useState<boolean | null>(null);
  const [locker, setLocker] = useState<boolean | null>(null);
  const [wifi, setWifi] = useState<boolean | null>(null);
  const [firstAid, setFirstAid] = useState<boolean | null>(null);
  const [washStation, setWashStation] = useState<boolean | null>(null);
  const [photography, setPhotography] = useState<boolean | null>(null);
  const [events, setEvents] = useState<boolean | null>(null);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState<string>('');

  // Get unique styles, levels and regions from all parks
  const allStyles = Array.from(new Set(parks.flatMap(p => p.styles || [])));
  const allLevels = Array.from(new Set(parks.flatMap(p => p.levels || [])));
  const allRegions = Array.from(new Set(parks.map(p => p.region))).sort();
  const difficulties = ['green', 'blue', 'red', 'black'];

  const applyFilters = () => {
    let filtered = [...parks];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.region.toLowerCase().includes(query) ||
        p.blurb.toLowerCase().includes(query)
      );
    }

    // Region filter
    if (selectedRegion) {
      filtered = filtered.filter(p => p.region === selectedRegion);
    }

    if (selectedRating !== null) {
      filtered = filtered.filter(p => p.rating >= selectedRating);
    }

    if (hasLift !== null) {
      filtered = filtered.filter(p => p.lift === hasLift);
    }

    if (familyFriendly !== null) {
      filtered = filtered.filter(p => p.familyFriendly === familyFriendly);
    }

    if (restaurant !== null) {
      filtered = filtered.filter(p => p.restaurant === restaurant);
    }

    if (accommodation !== null) {
      filtered = filtered.filter(p => p.accommodation === accommodation);
    }

    if (bikeService !== null && filtered.length > 0 && 'bikeService' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).bikeService === bikeService);
    }

    if (school !== null) {
      filtered = filtered.filter(p => p.school === school);
    }

    if (parking !== null && filtered.length > 0 && 'parking' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).parking === parking);
    }

    if (shop !== null && filtered.length > 0 && 'shop' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).shop === shop);
    }

    if (rental !== null && filtered.length > 0 && 'rental' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).rental === rental);
    }

    if (toilet !== null && filtered.length > 0 && 'toilet' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).toilet === toilet);
    }

    if (camping !== null && filtered.length > 0 && 'camping' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).camping === camping);
    }

    if (locker !== null && filtered.length > 0 && 'locker' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).locker === locker);
    }

    if (wifi !== null && filtered.length > 0 && 'wifi' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).wifi === wifi);
    }

    if (firstAid !== null && filtered.length > 0 && 'firstAid' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).firstAid === firstAid);
    }

    if (washStation !== null && filtered.length > 0 && 'washStation' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).washStation === washStation);
    }

    if (photography !== null && filtered.length > 0 && 'photography' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).photography === photography);
    }

    if (events !== null && filtered.length > 0 && 'events' in filtered[0]) {
      filtered = filtered.filter(p => (p as any).events === events);
    }

    if (selectedStyles.length > 0) {
      filtered = filtered.filter(p =>
        selectedStyles.some(style => p.styles?.includes(style))
      );
    }

    if (selectedLevels.length > 0) {
      filtered = filtered.filter(p =>
        selectedLevels.some(level => p.levels?.includes(level))
      );
    }

    if (selectedDifficulties.length > 0 && filtered.length > 0 && 'trails' in filtered[0]) {
      filtered = filtered.filter(p => {
        const park = p as Bikepark;
        return park.trails?.some(trail => selectedDifficulties.includes(trail.difficulty));
      });
    }

    // Price range filter
    if (selectedPriceRange) {
      filtered = filtered.filter(p => {
        if (!p.price) return selectedPriceRange === 'free';

        switch (selectedPriceRange) {
          case 'free':
            return p.price === 0;
          case '0-50':
            return p.price > 0 && p.price <= 50;
          case '51-100':
            return p.price >= 51 && p.price <= 100;
          case '101-150':
            return p.price >= 101 && p.price <= 150;
          case '150+':
            return p.price > 150;
          default:
            return true;
        }
      });
    }

    onFilterChange(filtered);
  };

  const toggleStyle = (style: string) => {
    const newStyles = selectedStyles.includes(style)
      ? selectedStyles.filter(s => s !== style)
      : [...selectedStyles, style];
    setSelectedStyles(newStyles);
  };

  const toggleLevel = (level: string) => {
    const newLevels = selectedLevels.includes(level)
      ? selectedLevels.filter(l => l !== level)
      : [...selectedLevels, level];
    setSelectedLevels(newLevels);
  };

  const toggleDifficulty = (diff: string) => {
    const newDiffs = selectedDifficulties.includes(diff)
      ? selectedDifficulties.filter(d => d !== diff)
      : [...selectedDifficulties, diff];
    setSelectedDifficulties(newDiffs);
  };

  // Debounced URL update for search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (searchQuery) {
        params.set('q', searchQuery);
      } else {
        params.delete('q');
      }
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchParams, pathname, router]);

  // Search handler
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedRegion('');
    setSelectedRating(null);
    setHasLift(null);
    setFamilyFriendly(null);
    setRestaurant(null);
    setAccommodation(null);
    setBikeService(null);
    setSchool(null);
    setParking(null);
    setShop(null);
    setRental(null);
    setToilet(null);
    setCamping(null);
    setLocker(null);
    setWifi(null);
    setFirstAid(null);
    setWashStation(null);
    setPhotography(null);
    setEvents(null);
    setSelectedStyles([]);
    setSelectedLevels([]);
    setSelectedDifficulties([]);
    setSelectedPriceRange('');

    // Clear URL params
    router.replace(pathname, { scroll: false });
  };

  // Expose reset function via ref
  useEffect(() => {
    if (onResetRef) {
      onResetRef.current = resetFilters;
    }
  }, [onResetRef]);

  // Apply filters whenever they change
  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedRegion, selectedRating, hasLift, familyFriendly, restaurant, accommodation, bikeService, school, parking, shop, rental, toilet, camping, locker, wifi, firstAid, washStation, photography, events, selectedStyles, selectedLevels, selectedDifficulties, selectedPriceRange]);

  return (
    <>
      {/* Search */}
      <div className="bp-sec">
            <h3>{t('search')}</h3>
            <div className="bp-ctrl">
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>

          {/* Basic Filters: Region + Rating */}
          <div className="bp-sec">
            <h3>Podstawowe filtry</h3>
          <>
            {/* Region */}
            <div style={{ marginTop: '8px' }}>
              <label className="bp-filter-label">
                {t('region')}
              </label>
              <div className="bp-ctrl">
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <option value="">{t('allRegions')}</option>
                  {allRegions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Rating */}
            <div style={{ marginTop: '12px' }}>
              <label className="bp-filter-label">
                {t('rating')}
              </label>
              <div className="bp-ctrl">
                <select
                  value={selectedRating || ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedRating(val);
                  }}
                >
                  <option value="">{t('all')}</option>
                  <option value="5">5 ★★★★★</option>
                  <option value="4">4+ ★★★★</option>
                  <option value="3">3+ ★★★</option>
                </select>
              </div>
            </div>
          </>
          </div>

          {/* Infrastructure Grid */}
          <div className="bp-sec">
            <h3>{t('infrastructure')}</h3>
          <div className="bp-infrastructure-grid">
          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={hasLift === true}
              onChange={(e) => setHasLift(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><CableCar size={16} /></span>
            <span className="bp-infra-label">{t('lift')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={familyFriendly === true}
              onChange={(e) => setFamilyFriendly(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Users size={16} /></span>
            <span className="bp-infra-label">{t('familyFriendly')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={restaurant === true}
              onChange={(e) => setRestaurant(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><UtensilsCrossed size={16} strokeWidth={2} /></span>
            <span className="bp-infra-label">{t('restaurant')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={accommodation === true}
              onChange={(e) => setAccommodation(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Hotel size={16} /></span>
            <span className="bp-infra-label">{t('accommodation')}</span>
          </label>

          {type === 'bikepark' && (
            <label className="bp-infra-card">
              <input
                type="checkbox"
                checked={bikeService === true}
                onChange={(e) => setBikeService(e.target.checked ? true : null)}
              />
              <span className="bp-infra-icon"><Wrench size={16} /></span>
              <span className="bp-infra-label">{t('bikeService')}</span>
            </label>
          )}

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={school === true}
              onChange={(e) => setSchool(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><GraduationCap size={16} /></span>
            <span className="bp-infra-label">{t('school')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={parking === true}
              onChange={(e) => setParking(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><ParkingCircle size={16} strokeWidth={2} /></span>
            <span className="bp-infra-label">{t('parking')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={shop === true}
              onChange={(e) => setShop(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><ShoppingCart size={16} /></span>
            <span className="bp-infra-label">{t('shop')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={rental === true}
              onChange={(e) => setRental(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Bike size={16} strokeWidth={2} /></span>
            <span className="bp-infra-label">{t('rental')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={toilet === true}
              onChange={(e) => setToilet(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Bath size={16} /></span>
            <span className="bp-infra-label">{t('toilet')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={camping === true}
              onChange={(e) => setCamping(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Tent size={16} /></span>
            <span className="bp-infra-label">{t('camping')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={locker === true}
              onChange={(e) => setLocker(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Lock size={16} /></span>
            <span className="bp-infra-label">{t('locker')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={wifi === true}
              onChange={(e) => setWifi(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Wifi size={16} /></span>
            <span className="bp-infra-label">{t('wifi')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={firstAid === true}
              onChange={(e) => setFirstAid(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Cross size={16} /></span>
            <span className="bp-infra-label">{t('medic')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={washStation === true}
              onChange={(e) => setWashStation(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Droplets size={16} /></span>
            <span className="bp-infra-label">{t('washStation')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={photography === true}
              onChange={(e) => setPhotography(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><Camera size={16} /></span>
            <span className="bp-infra-label">{t('photoSpots')}</span>
          </label>

          <label className="bp-infra-card">
            <input
              type="checkbox"
              checked={events === true}
              onChange={(e) => setEvents(e.target.checked ? true : null)}
            />
            <span className="bp-infra-icon"><PartyPopper size={16} /></span>
            <span className="bp-infra-label">{t('events')}</span>
          </label>
          </div>
          </div>

          {/* Styles */}
          {allStyles.length > 0 && (
            <div className="bp-sec">
              <h3>{t('styles')}</h3>
            <div className="bp-chips">
            {allStyles.map(style => (
              <button
                key={style}
                className={`bp-chip ${selectedStyles.includes(style) ? 'active' : ''}`}
                onClick={() => toggleStyle(style)}
              >
                {style}
              </button>
            ))}
            </div>
            </div>
          )}

          {/* Levels */}
          {allLevels.length > 0 && (
            <div className="bp-sec">
              <h3>{t('levels')}</h3>
            <div className="bp-chips">
            {allLevels.map(level => (
              <button
                key={level}
                className={`bp-chip bp-chip--${level} ${selectedLevels.includes(level) ? 'active' : ''}`}
                onClick={() => toggleLevel(level)}
              >
                {level}
              </button>
            ))}
            </div>
            </div>
          )}

          {/* Trail Difficulties (for bikeparks) */}
          {type === 'bikepark' && (
            <div className="bp-sec">
              <h3>{t('trailDifficulty')}</h3>
            <div className="bp-chips">
            {difficulties.map(diff => (
              <button
                key={diff}
                className={`bp-chip bp-chip--${diff} ${selectedDifficulties.includes(diff) ? 'active' : ''}`}
                onClick={() => toggleDifficulty(diff)}
              >
                {diff === 'green' && <><Circle size={12} fill="#22c55e" color="#22c55e" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('green')}</>}
                {diff === 'blue' && <><Circle size={12} fill="#3b82f6" color="#3b82f6" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('blue')}</>}
                {diff === 'red' && <><Circle size={12} fill="#ef4444" color="#ef4444" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('red')}</>}
                {diff === 'black' && <><Circle size={12} fill="#1f2937" color="#1f2937" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{t('black')}</>}
              </button>
            ))}
            </div>
            </div>
          )}

          {/* Price Range Filter */}
          <div className="bp-sec">
            <h3>{t('priceRange')}</h3>
          <div className="bp-chips">
          <button
            className={`bp-chip ${selectedPriceRange === 'free' ? 'active' : ''}`}
            onClick={() => setSelectedPriceRange(selectedPriceRange === 'free' ? '' : 'free')}
          >
            <Heart size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} /> {t('free')}
          </button>
          <button
            className={`bp-chip ${selectedPriceRange === '0-50' ? 'active' : ''}`}
            onClick={() => setSelectedPriceRange(selectedPriceRange === '0-50' ? '' : '0-50')}
          >
            <DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} /> 0-50 zł
          </button>
          <button
            className={`bp-chip ${selectedPriceRange === '51-100' ? 'active' : ''}`}
            onClick={() => setSelectedPriceRange(selectedPriceRange === '51-100' ? '' : '51-100')}
          >
            <DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /><DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} /> 51-100 zł
          </button>
          <button
            className={`bp-chip ${selectedPriceRange === '101-150' ? 'active' : ''}`}
            onClick={() => setSelectedPriceRange(selectedPriceRange === '101-150' ? '' : '101-150')}
          >
            <DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /><DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /><DollarSign size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '2px' }} /> 101-150 zł
          </button>
          <button
            className={`bp-chip ${selectedPriceRange === '150+' ? 'active' : ''}`}
            onClick={() => setSelectedPriceRange(selectedPriceRange === '150+' ? '' : '150+')}
          >
            <Gem size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> 150+ zł
          </button>
          </div>
          </div>
    </>
  );
}
